import { doc, getDoc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '@/firebase'
import { RULE_EXPECTATIONS, attempt, accessDetail, fmtExpected } from '../ruleExpectations.js'

// meetingDefaults/{schoolId} campus defaults. Never writes leftover
// meetingDefaults/global, and never stamps __smokeTest on a real school doc
// (cleanup would then delete live coordinators). District-tier write is
// probed on meetingDefaults/smoke_md_*; school-tier write uses the selected
// school and restores whatever was there.

const SCHOOL_TIER_ADMINS = ['school_admin', 'admin_504', 'sped_chair', 'sped_chair_all']

function defaultsPayload(docId, extra = {}) {
  return {
    schoolId: docId,
    coordinators: [],
    updatedAt: serverTimestamp(),
    ...extra
  }
}

async function restoreSchoolDefaults(ref, original) {
  if (original) await setDoc(ref, original)
  else await deleteDoc(ref)
}

export default {
  id: 'meetingDefaults',
  label: 'Meeting defaults read/write',
  description:
    'meetingDefaults/global stays read-only leftover; school docs follow admin + JWT campus. Does not keep changes on a real school.',
  destructive: true,

  async run(ctx, step) {
    const role = ctx.roleClaim
    const tokenSchoolIds = ctx.tokenSchoolIds
    const smokeId = `smoke_md_${ctx.runId.slice(-8)}`
    const smokeRef = doc(db, 'meetingDefaults', smokeId)
    const globalRef = doc(db, 'meetingDefaults', 'global')
    let smokeCreated = false
    let schoolTouched = false
    let schoolOriginal = null
    const schoolRef = ctx.schoolId ? doc(db, 'meetingDefaults', ctx.schoolId) : null

    const expectGlobal = RULE_EXPECTATIONS.meetingDefaultsWrite(role, {
      docId: 'global',
      tokenSchoolIds
    })
    const expectSmoke = RULE_EXPECTATIONS.meetingDefaultsWrite(role, {
      docId: smokeId,
      tokenSchoolIds
    })
    const expectRead = RULE_EXPECTATIONS.meetingDefaultsRead()

    try {
      await step(
        `write leftover meetingDefaults/global (expected: ${fmtExpected(expectGlobal)})`,
        async () => {
          const { allowed } = await attempt(() =>
            setDoc(globalRef, defaultsPayload('global', { coordinators: [] }))
          )
          if (allowed) await deleteDoc(globalRef).catch(() => {})
          return accessDetail(expectGlobal, allowed)
        }
      )

      await step(
        `write smoke meeting defaults (expected: ${fmtExpected(expectSmoke)})`,
        async () => {
          const { allowed } = await attempt(() =>
            setDoc(
              smokeRef,
              defaultsPayload(smokeId, {
                __smokeTest: true,
                __smokeRunId: ctx.runId
              })
            )
          )
          smokeCreated = allowed
          return allowed
            ? `${accessDetail(expectSmoke, allowed)} — meetingDefaults/${smokeId}`
            : accessDetail(expectSmoke, allowed)
        }
      )

      await step(`read smoke meeting defaults (expected: ${fmtExpected(expectRead)})`, async () => {
        const { allowed } = await attempt(() => getDoc(smokeRef))
        return accessDetail(expectRead, allowed)
      })

      if (smokeCreated) {
        await step(
          `delete smoke meeting defaults (expected: ${fmtExpected(expectSmoke)})`,
          async () => {
            const { allowed } = await attempt(() => deleteDoc(smokeRef))
            if (allowed) smokeCreated = false
            return accessDetail(expectSmoke, allowed)
          }
        )
      } else {
        step.skip('delete smoke meeting defaults', 'Write denied — nothing to delete')
      }

      const schoolTierAdmin = SCHOOL_TIER_ADMINS.includes(role)
      if (!schoolTierAdmin) {
        step.skip(
          'own-school meeting defaults',
          'District-tier / non-admin write is covered by the smoke_md_* and global probes. Real school docs are left untouched.'
        )
      } else if (!ctx.schoolId) {
        step.block(
          'own-school meeting defaults',
          'Select a school before probing school-tier meetingDefaults writes'
        )
      } else {
        const expectSchool = RULE_EXPECTATIONS.meetingDefaultsWrite(role, {
          docId: ctx.schoolId,
          tokenSchoolIds
        })
        await step(
          `write own-school meeting defaults (expected: ${fmtExpected(expectSchool)})`,
          async () => {
            const snap = await getDoc(schoolRef)
            schoolOriginal = snap.exists() ? snap.data() : null
            schoolTouched = true
            const { allowed } = await attempt(() =>
              setDoc(schoolRef, { schoolId: ctx.schoolId }, { merge: true })
            )
            return allowed
              ? `${accessDetail(expectSchool, allowed)} — meetingDefaults/${ctx.schoolId} (restored after)`
              : accessDetail(expectSchool, allowed)
          }
        )
      }
    } finally {
      await deleteDoc(smokeRef).catch(() => {})
      if (schoolTouched && schoolRef) {
        await restoreSchoolDefaults(schoolRef, schoolOriginal).catch(() => {})
      }
    }
  }
}
